/**
 * RouteTracker — pathname を購読して usePageView を発火するルート計測コンポーネント。
 *
 * 日記分析オーバーホール Phase 5「利用ログ修復＋無駄画面集計」(2026-05)。
 * 各ページコンポーネントに usePageView を個別に呼ぶ運用は漏れが出るため、
 * App.tsx の <Routes> 外側に <RouteTracker /> を1つ置いて、pathname の
 * 変化をフックして tab_clicks に UPSERT する。
 *
 * - pathname → 短い page key（'home' / 'journal' / 'me' / ...）に正規化する
 * - usePageView は内部で mount/unmount に recordPageView / recordPageDwell を
 *   呼ぶ。pathname が変わるたびに key が変わるので、自然に dwell が記録される。
 * - リダイレクトルート（/diary → /journal, /weekly → /me, /auth/google/callback 等）は
 *   遷移先の実 pathname だけが計測される（リダイレクト中の一瞬は無視）。
 * - 未知のパスは generic な 'unknown:<path>' で記録する（後でデッドゾーン集計時に
 *   気づけるように。ノイズで埋もれないよう先頭の1セグメントだけ拾う）。
 */

import { useLocation } from 'react-router-dom'
import { usePageView } from '@/lib/usageTelemetry'

/**
 * pathname を tab_clicks に書く短い page key に変換する。
 * 既知のページは Sidebar の `page` 値（NAV_ITEMS と同じ識別子）に揃える:
 *   '/' → 'home'
 *   '/journal' → 'journal'
 *   '/me' → 'me'
 *   '/partner' → 'partner', '/partner/history' → 'partner-history'
 *   '/artifacts/foo' → 'artifacts'（先頭1セグメントだけ）
 */
function pathnameToPageKey(pathname: string): string {
  const trimmed = pathname.replace(/^\/+|\/+$/g, '')
  if (trimmed === '') return 'home'

  // 認証コールバック・診断用ルートは計測対象外（特殊系の扱い）
  if (trimmed.startsWith('auth/google/callback')) return 'auth-callback'

  // 既知の2セグメントは結合した名前で記録する（'/partner/history' → 'partner-history'）
  // それ以外は先頭1セグメントだけを page key にする（artifacts/foo/bar → 'artifacts'）
  const segs = trimmed.split('/')
  if (segs.length >= 2 && segs[0] === 'partner' && segs[1] === 'history') {
    return 'partner-history'
  }
  const head = segs[0]
  // 識別子っぽくないもの（# でハッシュが残っている等）は防御的に切る
  return head.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 60) || 'home'
}

export function RouteTracker() {
  const location = useLocation()
  const pageKey = pathnameToPageKey(location.pathname)
  usePageView(pageKey)
  return null
}

// 単体テスト用に key 変換ロジックだけ export
export const __pathnameToPageKey = pathnameToPageKey
